import { Box, Stack } from '@mui/material';
import { useModalState } from '../../../../../../hooks/useModalState';
import { OrderConditionsTestIds } from '../../../../../../utils/testIds/OrderConditions.testIds';
import { UsedInOfflineMode } from '../../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../../translation/TranslationContext';
import OrderConditionSplitLabel from '../../../../conditions/components/OrderConditionSplitLabel';
import OrderInvoicesModal from '../../../../conditions/components/OrderInvoicesModal';
import {
  getConditionAvailability,
  getConditionSplitLabelParts,
} from '../../../../conditions/orderCondition.utils';
import { useOrderCalculateContext } from '../../../../contexts/OrderCalculate.context';
import { useOrderConditionContext } from '../../../../contexts/OrderCondition.context';
import { Order } from '../../../../order.types';
import OrderConditionDefinitionsModal from './components/OrderConditionDefinitionsModal/OrderConditionDefinitionsModal';
import OrderConditionDetailsForm from './components/OrderConditionDetailsForm/OrderConditionDetailsForm';
import OrderConditionDetailsReadOnly from './components/OrderConditionDetailsReadOnly/OrderConditionDetailsReadOnly';
import OrderConditionErrorIcon from './components/OrderConditionErrorIcon';
import OrderConditionLayout from './components/OrderConditionLayout';
import OrderConditionNavigator from './components/OrderConditionNavigator/OrderConditionNavigator';
import OrderConditionSummary from './components/OrderConditionSummary';
import OrderConditionsEmptyState from './components/OrderConditionsEmptyState';
import OrderConditionsErrorState from './components/OrderConditionsErrorState';

interface OrderObjectConditionsTabProps extends UsedInOfflineMode {
  order: Order;
  isReadOnlyView: boolean;
}

const OrderObjectConditionsTab = ({
  order,
  isReadOnlyView,
  isOfflineMode,
}: OrderObjectConditionsTabProps) => {
  const { translate } = useTranslation();
  const {
    orderConditions,
    selectedCondition,
    isLoadingOrderConditions,
    isErrorOrderConditions,
    invalidateOrderConditions,
  } = useOrderConditionContext();
  const { getConditionTotals } = useOrderCalculateContext();
  const definitionsModal = useModalState();
  const invoicesModal = useModalState();

  const condition = selectedCondition ?? orderConditions[0];

  if (isLoadingOrderConditions) {
    return (
      <Box
        aria-busy
        test-id={OrderConditionsTestIds.TAB_LOADING}
        sx={{ width: '100%', flex: '1 0 auto' }}
      />
    );
  }

  if (isErrorOrderConditions && !condition) {
    return <OrderConditionsErrorState onRetry={invalidateOrderConditions} />;
  }

  if (!condition) {
    return <OrderConditionsEmptyState isReadOnlyView={isReadOnlyView} />;
  }

  const totals = getConditionTotals(condition.id);

  return (
    <>
      <OrderConditionLayout
        navigator={
          <OrderConditionNavigator currentConditionId={condition.id}>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}
            >
              <OrderConditionSplitLabel
                availability={getConditionAvailability(condition.orderType)}
                {...getConditionSplitLabelParts(condition, translate)}
              />

              <OrderConditionErrorIcon conditionId={condition.id} />
            </Stack>
          </OrderConditionNavigator>
        }
        details={
          isReadOnlyView ? (
            <OrderConditionDetailsReadOnly
              order={order}
              condition={condition}
              isOfflineMode={isOfflineMode}
              onOpenMoreConditions={definitionsModal.openModal}
              onOpenOrderInvoices={invoicesModal.openModal}
            />
          ) : (
            <OrderConditionDetailsForm
              order={order}
              condition={condition}
              isOfflineMode={isOfflineMode}
              onOpenMoreConditions={definitionsModal.openModal}
              onOpenOrderInvoices={invoicesModal.openModal}
            />
          )
        }
        summary={totals ? <OrderConditionSummary totals={totals} /> : undefined}
      />

      {!isOfflineMode && (
        <>
          <OrderConditionDefinitionsModal
            order={order}
            condition={condition}
            isOpen={definitionsModal.isOpen}
            isReadOnlyView={isReadOnlyView}
            onClose={definitionsModal.closeModal}
          />

          <OrderInvoicesModal
            orderId={order.id}
            isOpen={invoicesModal.isOpen}
            onClose={invoicesModal.closeModal}
          />
        </>
      )}
    </>
  );
};

export default OrderObjectConditionsTab;
